import { zodResolver } from "@hookform/resolvers/zod";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
    Alert,
    Box,
    Button,
    Container,
    Grid,
    IconButton,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { RhfTextField } from "mui-rhf-integration";
import { enqueueSnackbar } from "notistack";
import { type ReactNode, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod/mini";
import { phoneNumberSchema, RhfPhoneField } from "@/components/RhfPhoneField/index.js";
import { RhfUsStateSelect } from "@/components/RhfUsStateSelect/index.js";
import { useCreateApiKeyMutation } from "@/mutations/api-key.js";
import { getErrorMessage } from "@/utils/api.js";

const schema = z.object({
    organization: z.string().check(z.trim(), z.minLength(1)),
    contact: z.object({
        firstName: z.string().check(z.trim(), z.minLength(1)),
        lastName: z.string().check(z.trim(), z.minLength(1)),
        title: z.string().check(z.trim(), z.minLength(1)),
        emailAddress: z.email(),
        phoneNumber: phoneNumberSchema,
    }),
    address: z.object({
        addressLineOne: z.string().check(z.trim(), z.minLength(1)),
        addressLineTwo: z._default(z.string().check(z.trim()), ""),
        city: z.string().check(z.trim(), z.minLength(1)),
        state: z.string().check(z.trim(), z.minLength(1)),
        zipCode: z.string().check(z.trim(), z.regex(/^\d{5}(?:-\d{4})?$/, "Invalid ZIP code")),
    }),
    estimatedDeviceCount: z.coerce.number().check(z.int(), z.nonnegative()),
});
type FieldValues = z.input<typeof schema>;
type TransformedValues = z.output<typeof schema>;

const Root = (): ReactNode => {
    const [apiKeyToken, setApiKeyToken] = useState<string | null>(null);
    const createMutation = useCreateApiKeyMutation();

    const form = useForm<FieldValues, unknown, TransformedValues>({
        resolver: zodResolver(schema, {
            error: (iss) => {
                if (
                    iss.code === "invalid_type" &&
                    (iss.input === undefined || iss.input === null)
                ) {
                    return "Required";
                }

                if (iss.code === "invalid_type" && iss.expected === "int") {
                    return "Invalid number";
                }

                if (iss.code === "too_small" && iss.origin === "number") {
                    return `Must be at least ${iss.minimum}`;
                }

                if (iss.code === "too_small" && iss.origin === "string" && iss.minimum === 1) {
                    return "Required";
                }

                return iss.message;
            },
        }),
    });

    const handleSubmit = useCallback(
        async (values: TransformedValues) => {
            try {
                const token = await createMutation.mutateAsync(values);
                setApiKeyToken(token);
            } catch (error) {
                enqueueSnackbar(getErrorMessage(error), { variant: "error" });
            }
        },
        [createMutation],
    );

    if (apiKeyToken) {
        return (
            <Container maxWidth="md">
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h5" textAlign="center">
                        Request Submitted Successfully!
                    </Typography>
                    <Typography textAlign="center">
                        Your API access token has been generated and is now available. Please store
                        it securely, as it will not be shown again.
                    </Typography>

                    <Paper variant="outlined" sx={{ my: 2, p: 2, bgcolor: "grey.100" }}>
                        <ListItem
                            component="div"
                            disableGutters
                            disablePadding
                            secondaryAction={
                                <IconButton
                                    onClick={() => {
                                        if (apiKeyToken) {
                                            void navigator.clipboard.writeText(apiKeyToken);
                                        }
                                    }}
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                            }
                        >
                            <ListItemText
                                primary="Access Token"
                                secondary={apiKeyToken}
                                slotProps={{
                                    secondary: {
                                        fontFamily: "monospace",
                                        fontSize: "1.25rem",
                                        color: "inherit",
                                    },
                                }}
                            />
                        </ListItem>
                    </Paper>

                    <Typography textAlign="center">
                        Your token is currently inactive and will become active once your request is
                        reviewed and approved. You’ll receive an email notification when activation
                        is complete.
                    </Typography>

                    <Box sx={{ mt: 4, textAlign: "center" }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setApiKeyToken(null);
                                form.reset();
                            }}
                        >
                            Submit Another Request
                        </Button>
                    </Box>
                </Paper>
            </Container>
        );
    }

    return (
        <Container
            maxWidth="md"
            component="form"
            noValidate
            onSubmit={form.handleSubmit(handleSubmit)}
        >
            <Stack spacing={2}>
                <div>
                    <Typography variant="h5" textAlign="center">
                        Request API Access Token
                    </Typography>
                    <Typography textAlign="center">
                        Complete the form below to generate your API access token. Your token will
                        be created immediately, but it will remain inactive until your request has
                        been reviewed and approved.
                    </Typography>
                </div>
                <Alert severity="info">
                    For security purposes, all token requests undergo manual review. Your token will
                    be activated once approved, and you’ll receive an email notification when it
                    becomes active.
                </Alert>
                <Paper sx={{ p: 2 }} variant="outlined">
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Organization Details
                    </Typography>
                    <RhfTextField
                        label="Organization Name"
                        required
                        control={form.control}
                        name="organization"
                        fullWidth
                        autoComplete="organization"
                    />
                </Paper>
                <Paper sx={{ p: 2 }} variant="outlined">
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Contact Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <RhfTextField
                                label="First Name"
                                required
                                control={form.control}
                                name="contact.firstName"
                                fullWidth
                                autoComplete="given-name"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <RhfTextField
                                label="Last Name"
                                required
                                control={form.control}
                                name="contact.lastName"
                                fullWidth
                                autoComplete="family-name"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <RhfTextField
                                label="Job Title"
                                required
                                control={form.control}
                                name="contact.title"
                                fullWidth
                                autoComplete="organization-title"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <RhfPhoneField
                                label="Phone Number"
                                required
                                control={form.control}
                                name="contact.phoneNumber"
                                fullWidth
                            />
                        </Grid>
                        <Grid size={12}>
                            <RhfTextField
                                label="Email Address"
                                required
                                control={form.control}
                                name="contact.emailAddress"
                                fullWidth
                                autoComplete="email"
                                slotProps={{
                                    input: { inputMode: "email" },
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
                <Paper sx={{ p: 2 }} variant="outlined">
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Address Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <RhfTextField
                                label="Adress Line 1"
                                required
                                control={form.control}
                                name="address.addressLineOne"
                                fullWidth
                                autoComplete="address-line1"
                            />
                        </Grid>
                        <Grid size={12}>
                            <RhfTextField
                                label="Adress Line 2"
                                control={form.control}
                                name="address.addressLineTwo"
                                fullWidth
                                autoComplete="address-line2"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <RhfTextField
                                label="City"
                                required
                                control={form.control}
                                name="address.city"
                                fullWidth
                                autoComplete="address-level2"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <RhfUsStateSelect
                                control={form.control}
                                name="address.state"
                                textFieldProps={{
                                    label: "State",
                                    required: true,
                                    autoComplete: "address-level1",
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <RhfTextField
                                label="ZIP Code"
                                required
                                control={form.control}
                                name="address.zipCode"
                                fullWidth
                                autoComplete="postal-code"
                            />
                        </Grid>
                    </Grid>
                </Paper>
                <Paper sx={{ p: 2 }} variant="outlined">
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Usage Information
                    </Typography>
                    <RhfTextField
                        label="Estimated Device Count"
                        required
                        control={form.control}
                        name="estimatedDeviceCount"
                        fullWidth
                        helperText="Approximate number of devices you plan to query pricing information for"
                        slotProps={{
                            input: { inputMode: "numeric" },
                        }}
                    />
                </Paper>
            </Stack>

            <Box sx={{ mt: 4, textAlign: "center" }}>
                <Button type="submit" variant="contained" loading={createMutation.isPending}>
                    Submit Token Request
                </Button>
            </Box>
        </Container>
    );
};

export const Route = createFileRoute("/")({
    component: Root,
});
